import * as Sentry from '@sentry/react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Component } from 'react';

/**
 * Error boundary component to catch and handle React errors with Sentry integration
 * @class ErrorBoundary
 * @extends Component
 */
class ErrorBoundary extends Component {
  /**
   * Create an ErrorBoundary instance
   * @param {Object} props - Component props
   * @param {React.ReactNode} props.children - Child components to wrap
   * @param {Function} [props.onError] - Optional error callback
   * @param {React.ReactNode} [props.fallback] - Custom fallback UI
   * @param {boolean} [props.showDetails=false] - Whether to show error details in production
   * @param {boolean} [props.enableSentry=true] - Whether to send errors to Sentry
   */
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      sentryEventId: null,
    };
  }

  /**
   * Static method to update state when an error occurs
   * @param {Error} error - The error that occurred
   * @returns {Object} New state object
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Lifecycle method called when an error occurs
   * @param {Error} error - The error that occurred
   * @param {Object} errorInfo - Additional error information
   * @param {string} errorInfo.componentStack - Component stack trace
   */
  componentDidCatch(error, errorInfo) {
    // Log error details to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Generate local event ID
    const localEventId = this.generateEventId();

    // Report error to Sentry and get Sentry event ID
    let sentryEventId = null;
    if (this.props.enableSentry !== false) {
      sentryEventId = this.reportToSentry(error, errorInfo);
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
      eventId: localEventId,
      sentryEventId,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error report (for non-Sentry logging/monitoring)
    this.logErrorReport(error, errorInfo, localEventId, sentryEventId);
  }

  /**
   * Report error to Sentry
   * @param {Error} error - The error to report
   * @param {Object} errorInfo - Additional error information
   * @returns {string|null} Sentry event ID or null if Sentry is not available
   */
  reportToSentry = (error, errorInfo) => {
    try {
      // Capture exception with Sentry and get event ID
      const eventId = Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        level: 'error',
        tags: {
          errorBoundary: true,
          component: 'ErrorBoundary',
        },
      });

      console.log('Error reported to Sentry with ID:', eventId);
      return eventId;
    } catch (sentryError) {
      console.error('Failed to report error to Sentry:', sentryError);
      return null;
    }
  };

  /**
   * Generate a unique event ID for error tracking
   * @returns {string} Unique event ID
   */
  generateEventId = () => {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Log error report to console (for additional monitoring services)
   * @param {Error} error - The error to report
   * @param {Object} errorInfo - Additional error information
   * @param {string} localEventId - Local event ID
   * @param {string|null} sentryEventId - Sentry event ID
   */
  logErrorReport = (error, errorInfo, localEventId, sentryEventId) => {
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      localEventId,
      sentryEventId,
    };

    // Log to console in development
    if (import.meta.env.MODE === 'development') {
      console.log('Error report:', errorReport);
    }

    // TODO: Send to additional error monitoring service if needed
    // Example: Send to your backend logging service
    // this.sendToBackendLogger(errorReport);
  };

  /**
   * Reset the error boundary state
   */
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      sentryEventId: null,
    });
  };

  /**
   * Reload the current page
   */
  reloadPage = () => {
    window.location.reload();
  };

  /**
   * Navigate to home page
   */
  goHome = () => {
    window.location.href = '/dashboard';
  };

  /**
   * Open Sentry feedback dialog
   */
  openSentryFeedback = () => {
    if (this.state.sentryEventId) {
      Sentry.showReportDialog({
        eventId: this.state.sentryEventId,
        user: {
          email: '', // Pre-fill if user email is available
          name: '', // Pre-fill if user name is available
        },
        title: "It looks like we're having issues.",
        subtitle: 'Our team has been notified.',
        subtitle2: "If you'd like to help, tell us what happened below.",
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelClose: 'Close',
        labelSubmit: 'Submit',
        errorGeneric:
          'An error occurred while submitting your report. Please try again.',
        errorFormEntry:
          'Some fields were invalid. Please correct the errors and try again.',
        successMessage: 'Your feedback has been sent. Thank you!',
      });
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-xl sm:rounded-lg sm:px-10">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="rounded-full bg-red-100 p-3">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>

              {/* Error Title */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Oops! Something went wrong
                </h2>
                <p className="text-sm text-gray-600">
                  We're sorry for the inconvenience. The application encountered
                  an unexpected error.
                </p>
                {this.state.sentryEventId && (
                  <p className="text-xs text-gray-500 mt-2">
                    This error has been automatically reported to our team.
                  </p>
                )}
              </div>

              {/* Error Details */}
              {(import.meta.env.MODE === 'development' ||
                this.props.showDetails) &&
                this.state.error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                    <h3 className="text-sm font-medium text-red-800 mb-2">
                      Error Details:
                    </h3>
                    <div className="text-xs text-red-700 font-mono break-all">
                      <p className="mb-2">
                        <strong>Message:</strong> {this.state.error.message}
                      </p>
                      {this.state.eventId && (
                        <p className="mb-2">
                          <strong>Local Event ID:</strong> {this.state.eventId}
                        </p>
                      )}
                      {this.state.sentryEventId && (
                        <p className="mb-2">
                          <strong>Sentry Event ID:</strong>{' '}
                          {this.state.sentryEventId}
                        </p>
                      )}
                      {this.state.error.stack && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-red-600 hover:text-red-800">
                            Stack Trace
                          </summary>
                          <pre className="mt-2 whitespace-pre-wrap text-xs bg-red-100 p-2 rounded overflow-auto max-h-32">
                            {this.state.error.stack}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={this.resetError}
                  className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </button>

                <button
                  onClick={this.reloadPage}
                  className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Page
                </button>

                <button
                  onClick={this.goHome}
                  className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </button>

                {/* Sentry Feedback Button */}
                {this.state.sentryEventId && (
                  <button
                    onClick={this.openSentryFeedback}
                    className="w-full flex justify-center items-center py-2 px-4 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Report Issue
                  </button>
                )}
              </div>

              {/* Support Information */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  If this problem persists, please contact support
                  {this.state.sentryEventId && (
                    <>
                      {' '}
                      with Error ID:{' '}
                      <span className="font-mono">
                        {this.state.sentryEventId}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
